import type { FieldHelperProps, FieldInputProps, FieldMetaProps } from "formik";
import { cloneDeep, intersection, uniq } from "lodash";
import moment from "moment";
import { useState } from "react";
import type { TFunction } from "src/design-system/core";
import { ChannelSourceEnum } from "src/types/channel";
import { ALL_DAYS_OF_THE_WEEK } from "src/types/enum/DayOfTheWeek";
import { OfferTypeEnum } from "src/types/offer";
import type { Trigger } from "src/types/trigger";
import { getDaysBetween } from "src/utils/dateUtils";
import { UpdateChannelValidationDialog } from "src/views/marketing-actions/components/dialogs/UpdateChannelValidationDialog";
import type { OfferScenarioCharacteristicInput } from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormInput";
import type { MultipleAutocompleteProps } from "../components/form";
export interface MultipleSelectFieldType {
    name?: string;
    autoCompleteProps?: Omit<MultipleAutocompleteProps, "name">;
}

export function resetFieldsFonction<T extends Record<string, unknown>, K extends keyof T>(
    resetFields: K[],
    {
        field,
        meta,
        helpers,
        fullField,
    }: {
        field: FieldInputProps<any>;
        meta: FieldMetaProps<any>;
        helpers: FieldHelperProps<any>;
        fullField?: boolean;
    },
    values?: T
): void {
    const newField = resetFields.reduce((acc, elm) => {
        if (fullField) {
            return {
                ...acc,
                ...meta.initialValue,
                ...(values ?? {}),
            };
        } else {
            return {
                ...acc,
                [elm]:
                    (!values && values[elm]) === undefined ? meta.initialValue[elm] : values[elm],
            };
        }
    }, {});
    const newValue = {
        ...field.value,
        ...newField,
    };
    helpers.setValue(newValue);
}

export function getFilledTrigger(
    triggerStart: Partial<Trigger>,
    triggerStop: Partial<Trigger>
): Partial<Trigger> {
    if (triggerStart && triggerStart.metaData) {
        return triggerStart;
    }
    if (triggerStop && triggerStop.metaData) {
        return triggerStop;
    }
    return null;
}

export const resetOfferCharacteristic = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    isSpecial: boolean
): void => {
    setFieldValue("offerCharacteristic.cart", null, false);
    setFieldValue("offerCharacteristic.discount", null, false);
    setFieldValue("offerCharacteristic.isCofunded", false, false);
    setFieldValue("offerCharacteristic.isSpecial", isSpecial, false);
    setFieldValue("offerCharacteristic.isSpecialString", isSpecial.toString(), false);
    setFieldValue("offerCharacteristic.products", [], false);
    setFieldValue("offerCharacteristic.specialExistOnFlynt", false, false);
    setFieldValue("offerCharacteristic.specialOperation", null, false);
    setFieldValue("offerCharacteristic.value", null, false);
};

export function checkIfBlockPreorder(
    scenarios: OfferScenarioCharacteristicInput[],
    channel: ChannelSourceEnum
): boolean {
    if (channel !== ChannelSourceEnum.Ubereats) {
        return false;
    }

    for (const scenario of scenarios) {
        if (
            [
                OfferTypeEnum.DiscountByItem,
                OfferTypeEnum.Bogo,
                OfferTypeEnum.FreeItem,
                OfferTypeEnum.SpecificDish,
            ].includes(scenario.value)
        ) {
            return true;
        }
    }

    return false;
}

interface AlertPeriodProps {
    startDateComparePeriod: Date;
    endDateComparePeriod: Date;
    days: any;
}

export function detectMissingDaysInStartAndEndComparePeriod({
    startDateComparePeriod,
    endDateComparePeriod,
    days,
}: AlertPeriodProps): Date[] {
    // cherche si dans la période de comp qui est composé de startDateComparePeriod et endDateComparePeriod
    // trouve les jours manquant dans la compare période avec la liste days
    const startDay = moment(startDateComparePeriod);
    const endDay = moment(endDateComparePeriod);
    const daysInComparePeriod = [];
    for (let m = moment(startDay); m.diff(endDay, "days") <= 0; m.add(1, "days")) {
        daysInComparePeriod.push(m.day());
    }
    const uniqDaysInComparePeriod = uniq(daysInComparePeriod);
    if (days.length !== uniqDaysInComparePeriod.length) {
        return days.filter((day) => !uniqDaysInComparePeriod.includes(day));
    }
    return [];
}

export function getTriggerReferenceValues(
    currentValueList: any[],
    initialData: any[],
    {
        specificValueAutoRulePayload,
        defaultValuePerPos,
        currentFormValues,
        currentTriggerValue,
        currentTrigger,
    }: {
        specificValueAutoRulePayload: any[];
        defaultValuePerPos: any;
        currentFormValues: any[];
        currentTriggerValue: any;
        currentTrigger: any;
    }
): any {
    const alreadyExistByPosIdIndex = currentValueList?.findIndex(
        (e) => e.posId === defaultValuePerPos.posId
    );
    const currentValue =
        specificValueAutoRulePayload?.find((e) => e.posId === defaultValuePerPos.posId) ?? [];
    const currentFormValue =
        currentFormValues?.find((e) => e.posId === defaultValuePerPos.posId) ?? [];
    let mergeValues;
    if (currentFormValue && currentFormValue[currentTrigger]) {
        mergeValues = {
            ...cloneDeep(defaultValuePerPos.averagePerDay),
            ...currentFormValue[currentTrigger],
        };
    } else {
        mergeValues = {
            ...ALL_DAYS_OF_THE_WEEK.reduce((acc, day) => {
                const currentValueFounded = Object.values(currentValue)?.find(
                    (e: any) => e.trigger === currentTrigger && e.dow === day
                );
                const defaultValueFounded = defaultValuePerPos?.averagePerDay[`day_${day}`];
                if (currentValueFounded) {
                    return {
                        ...acc,
                        [`day_${day}`]: (currentValueFounded as any)?.value,
                    };
                }
                return {
                    ...acc,
                    [`day_${day}`]: defaultValueFounded * (1 + currentTriggerValue / 100),
                };
            }, {}),
        };
    }

    if (alreadyExistByPosIdIndex === -1 || !currentValueList[alreadyExistByPosIdIndex]) {
        initialData.push({
            posName: defaultValuePerPos.pos,
            posId: defaultValuePerPos.posId,
            [currentTrigger]: cloneDeep(defaultValuePerPos.averagePerDay),
        });
        currentValueList.push({
            posName: defaultValuePerPos.pos,
            posId: defaultValuePerPos.posId,
            [currentTrigger]: mergeValues,
        });
        return;
    }
    currentValueList[alreadyExistByPosIdIndex][currentTrigger] = mergeValues;
    initialData[alreadyExistByPosIdIndex][currentTrigger] = cloneDeep(
        defaultValuePerPos.averagePerDay
    );
}

export type SetModalResetFieldDisplay = (
    a: { functionToConfirm?: () => void; functionToCancel?: () => void; messageToConfirm?: string },
    meta: FieldMetaProps<any>,
    options?: { forceDisplayModal: boolean }
) => void;

export function useResetFielModal(
    t: TFunction,
    {
        onConfirmFunction,
    }: {
        onConfirmFunction?: () => void;
    }
): {
    Modal: React.ReactNode;
    modalResetFieldDisplay: boolean;
    setModalResetFieldDisplay: SetModalResetFieldDisplay;
} {
    const defaultMessage = "reference-values-update-dialog";
    const [modalResetFieldDisplay, setModalResetFieldDisplay] = useState(false);
    const [messageToConfirm, setMessageToConfirm] = useState(defaultMessage);
    const [{ functionToConfirmModal }, setFunctionToConfirmModal] = useState({
        functionToConfirmModal: undefined,
    });
    const [{ functionToCancelModal }, setFunctionToCancelModal] = useState({
        functionToCancelModal: undefined,
    });

    function setConfirmAfterModal(
        {
            functionToConfirm,
            functionToCancel,
            messageToConfirm,
        }: Parameters<SetModalResetFieldDisplay>[0],
        meta: Parameters<SetModalResetFieldDisplay>[1],
        { forceDisplayModal = false }: { forceDisplayModal?: boolean } = {}
    ): void {
        if (functionToConfirm) {
            if (messageToConfirm) {
                setMessageToConfirm(messageToConfirm);
            }
            setFunctionToConfirmModal({ functionToConfirmModal: functionToConfirm });
        }
        if (functionToCancel) {
            setFunctionToCancelModal({ functionToCancelModal: functionToCancel });
        }

        if (meta?.touched || forceDisplayModal) {
            setModalResetFieldDisplay(true);
        } else {
            if (onConfirmFunction) {
                onConfirmFunction();
            }
            functionToConfirm();
        }
    }

    const Modal = (
        <UpdateChannelValidationDialog
            text={messageToConfirm}
            isOpen={modalResetFieldDisplay}
            onCancel={() => {
                setMessageToConfirm(defaultMessage);
                if (functionToCancelModal) {
                    functionToCancelModal();
                }
                setModalResetFieldDisplay(false);
            }}
            onSave={() => {
                if (onConfirmFunction) {
                    onConfirmFunction();
                }
                setModalResetFieldDisplay(false);
                if (functionToConfirmModal) {
                    functionToConfirmModal();
                }
            }}
            t={t}
        />
    );

    return {
        Modal,
        modalResetFieldDisplay,
        setModalResetFieldDisplay: setConfirmAfterModal,
    };
}

export function getDiffDaysFromIntervalAndDays(
    {
        endDate,
        startDate,
    }: {
        startDate?: moment.Moment;
        endDate?: moment.Moment;
    },
    days: number[]
): number[] {
    return !endDate
        ? days
        : intersection(getDaysBetween(moment(startDate).toDate(), moment(endDate).toDate()), days);
}
